import {Injectable} from '@angular/core';
import {TutorialCampaignDefinition} from "./models/tutorial-campaign-definition.interface";

@Injectable({
  providedIn: 'root'
})
export class TutorialRegistryService {
  private readonly campaigns: TutorialCampaignDefinition[] = [
    {
      key: 'home',
      version: 2,
      priority: 10,
      waitTimeoutMs: 3500,
      steps: [
        'tutorial-footer-home',
        'tutorial-footer-vehicles',
        'tutorial-footer-events',
        'tutorial-footer-ranking',
        'tutorial-footer-marketplace',
        'tutorial-footer-search',
        'tutorial-footer-chat',
        'tutorial-footer-profile',
        'tutorial-breadcrumb-profile',
        'tutorial-breadcrumb-garage',
        'tutorial-topbar-menu',
        'tutorial-account-shortcuts',
        'tutorial-topbar-search',
        'tutorial-topbar-notifications-desktop',
        'tutorial-home-timeline',
      ],
      matches: (url: string) => url === '/',
    },
    {
      key: 'garage',
      version: 2,
      priority: 10,
      waitTimeoutMs: 3500,
      steps: [
        'tutorial-topbar-menu',
        'tutorial-account-shortcuts',
        'tutorial-breadcrumb-back',
        'tutorial-breadcrumb-scrolltop',
        'tutorial-breadcrumb-profile',
        'tutorial-breadcrumb-garage',
        'tutorial-breadcrumb-view-toggle',
        'tutorial-garage-header',
        'tutorial-garage-profile-link',
        'tutorial-garage-projects',
        'tutorial-footer-add',
        'tutorial-garage-create-project',
        'tutorial-garage-create-collaboration',
        'tutorial-footer-profile',
      ],
      beforeStart: () => {
        const addButton = document.querySelector('[data-tutorial="tutorial-footer-add"]') as HTMLElement | null;
        addButton?.click();
      },
      matches: (url: string) => /^\/[^/]+\/garage$/.test(url),
    },
    {
      key: 'profile',
      version: 2,
      priority: 10,
      waitTimeoutMs: 3500,
      steps: [
        'tutorial-breadcrumb-back',
        'tutorial-breadcrumb-scrolltop',
        'tutorial-topbar-search',
        'tutorial-profile-social',
        'tutorial-profile-stats',
        'tutorial-profile-tabs',
        'tutorial-profile-summary-garage',
        'tutorial-profile-summary-highlight',
        'tutorial-profile-summary-events',
        'tutorial-footer-home',
      ],
      matches: (url: string) => /^\/[^/]+\/profile(\/[^/]+)?$/.test(url),
    },
    {
      key: 'project',
      version: 2,
      priority: 10,
      waitTimeoutMs: 4000,
      steps: [
        'tutorial-breadcrumb-back',
        'tutorial-breadcrumb-scrolltop',
        'tutorial-breadcrumb-profile',
        'tutorial-breadcrumb-garage',
        'tutorial-project-header',
        'tutorial-project-actions',
        'tutorial-project-tabs',
        'tutorial-project-collaborations',
      ],
      matches: (url: string) => /^\/[^/]+\/garage\/[^/]+$/.test(url),
    },
    {
      key: 'feed',
      version: 2,
      priority: 10,
      waitTimeoutMs: 3000,
      steps: [
        'tutorial-breadcrumb-back',
        'tutorial-breadcrumb-scrolltop',
        'tutorial-breadcrumb-profile',
        'tutorial-breadcrumb-garage',
        'tutorial-topbar-search',
        'tutorial-feed-list',
        'tutorial-footer-home',
        'tutorial-footer-chat',
      ],
      matches: (url: string) => url === '/feed',
    },
    {
      key: 'events-list',
      version: 2,
      priority: 10,
      waitTimeoutMs: 4000,
      steps: [
        'tutorial-breadcrumb-back',
        'tutorial-breadcrumb-scrolltop',
        'tutorial-breadcrumb-profile',
        'tutorial-breadcrumb-garage',
        'tutorial-events-filters',
        'tutorial-events-mode',
        'tutorial-events-list',
        'tutorial-footer-add',
      ],
      matches: (url: string) => url === '/events',
    },
    {
      key: 'events-detail',
      version: 2,
      priority: 10,
      waitTimeoutMs: 4000,
      steps: [
        'tutorial-breadcrumb-back',
        'tutorial-breadcrumb-scrolltop',
        'tutorial-breadcrumb-profile',
        'tutorial-breadcrumb-garage',
        'tutorial-event-detail-actions',
        'tutorial-event-detail-tabs',
        'tutorial-event-detail-content',
      ],
      matches: (url: string) => /^\/events\/[^/]+$/.test(url),
    },
    {
      key: 'marketplace',
      version: 2,
      priority: 10,
      waitTimeoutMs: 3500,
      steps: [
        'tutorial-breadcrumb-back',
        'tutorial-breadcrumb-scrolltop',
        'tutorial-breadcrumb-profile',
        'tutorial-breadcrumb-garage',
        'tutorial-marketplace-search',
        'tutorial-marketplace-advanced',
        'tutorial-marketplace-observed',
        'tutorial-footer-chat',
      ],
      matches: (url: string) => url === '/marketplace',
    },
    {
      key: 'offers',
      version: 1,
      priority: 10,
      waitTimeoutMs: 3500,
      steps: [
        'tutorial-account-shortcuts',
        'tutorial-breadcrumb-back',
        'tutorial-breadcrumb-scrolltop',
        'tutorial-breadcrumb-profile',
        'tutorial-breadcrumb-garage',
        'tutorial-offers-list',
        'tutorial-footer-add',
      ],
      matches: (url: string) => /^\/[^/]+\/offers$/.test(url),
    },
    {
      key: 'chat',
      version: 1,
      priority: 10,
      waitTimeoutMs: 4000,
      steps: [
        'tutorial-chat-filters',
        'tutorial-chat-threads',
        'tutorial-chat-messages',
        'tutorial-chat-input',
      ],
      matches: (url: string) => /^\/chat(\/[^/]+)?$/.test(url),
    },
    {
      key: 'settings',
      version: 2,
      priority: 10,
      waitTimeoutMs: 3500,
      steps: [
        'tutorial-account-shortcuts',
        'tutorial-breadcrumb-back',
        'tutorial-breadcrumb-scrolltop',
        'tutorial-breadcrumb-profile',
        'tutorial-breadcrumb-garage',
        'tutorial-settings-account',
        'tutorial-settings-profile',
        'tutorial-settings-preferences',
        'tutorial-settings-restart',
      ],
      matches: (url: string) => /^\/[^/]+\/settings$/.test(url),
    },
  ];

  public getMatchingCampaign(url: string): TutorialCampaignDefinition | null {
    const normalizedUrl = this.normalizeUrl(url);
    const campaign = this.campaigns
      .filter((campaign) => campaign.matches(normalizedUrl))
      .sort((a, b) => (b.priority || 0) - (a.priority || 0))[0] || null;

    return this.resolveResponsiveCampaign(campaign);
  }

  public getCampaignByKey(key: string): TutorialCampaignDefinition | null {
    return this.resolveResponsiveCampaign(this.campaigns.find((campaign) => campaign.key === key) || null);
  }

  public getCampaignKeys(): string[] {
    return this.campaigns.map((campaign) => campaign.key);
  }

  private resolveResponsiveCampaign(campaign: TutorialCampaignDefinition | null): TutorialCampaignDefinition | null {
    if (!campaign) {
      return null;
    }

    if (campaign.key !== 'home' || !this.isMobileViewport()) {
      return campaign;
    }

    return {
      ...campaign,
      steps: [
        'tutorial-footer-home',
        'tutorial-footer-vehicles',
        'tutorial-footer-events',
        'tutorial-footer-ranking',
        'tutorial-footer-marketplace',
        'tutorial-footer-search',
        'tutorial-footer-chat',
        'tutorial-footer-profile',
        'tutorial-breadcrumb-profile',
        'tutorial-breadcrumb-garage',
        'tutorial-topbar-menu',
        'tutorial-account-shortcuts',
        'tutorial-topbar-search',
      ],
    };
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < 768;
  }

  private normalizeUrl(url: string): string {
    return (url || '').split('#')[0].split('?')[0];
  }
}
